import React, { useEffect, useRef, useState } from "react";
import { motion } from 'framer-motion';
import { Fade } from "react-awesome-reveal";
import { MdChevronRight } from "react-icons/md";

import "../../styling.css";
import BackgroundCover from "../../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../../components/TitleWithDescription/TitleWithDescription";
import { isMobile } from "../../../utils/appUtils";
import Title from "../../../components/Title/Title";
import Collection from "../../../components/Collection/Collection";
import { useLanguage } from "../../../context/LanguageContext";
import { getTourmalineTranslations } from "../../../translations/tourmalineTemplate";
import { getTourmalineCollectionItems } from "../../../translations/tourmalineCollectionItems";
import { getTourmalineFrequencies } from "../../../translations/tourmalineFrequencies";

function TourmalineCollection() {
    const { language } = useLanguage();
    const t = getTourmalineTranslations(language);

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerChildren = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    // Holds which frequency is currently expanded (if any)
    const [expandedFrequency, setExpandedFrequency] = useState<string | null>(null);

    // Get frequencies from translations
    const standardFrequencies = getTourmalineFrequencies(language);

    const [expandedCertificate, setExpandedCertificate] = useState<string | null>(null);

    const toggleFrequency = (id: string) => {
        setExpandedFrequency(expandedFrequency === id ? null : id);
    };

    const openCertificate = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedCertificate(id);
    };

    const closeCertificate = () => {
        setExpandedCertificate(null);
    };

    // Certificate Modal Component
    const CertificateModal = ({ certificateUrl, onClose }: { certificateUrl: string; onClose: () => void }) => {
        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <div className="relative max-w-6xl w-full h-auto">
                    <button
                        onClick={onClose}
                        className="absolute -top-10 right-0 text-white hover:text-gray-300"
                    >
                        Close ×
                    </button>
                    <img
                        src={certificateUrl}
                        alt="Frequency Certificate"
                        className="w-full h-auto rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>
        );
    };

    const renderContent = () => {
        const fadeDuration = 2000;

        return (
            <>
                {/* Tourmaline Frequencies */}
                <div className="py-4 md:py-8">
                    <TitleWithDescription
                        title={{
                            firstLine: t.frequenciesTitle,
                            secondLine: t.frequenciesSubtitle
                        }}
                        description={t.frequenciesDescription}
                        layout="row"
                        className="px-4 md:px-8 lg:px-24"
                    />
                </div>

                {/* Vibrate in Frequency */}
                <TitleWithDescription
                    title={{
                        firstLine: t.oscillationTitle,
                        secondLine: t.oscillationSubtitle
                    }}
                    description={t.oscillationDescription}
                    layout="column"
                    image={{
                        src: "/images/jewellery_placeholder.png",
                        alt: "Tourmaline jewelry piece",
                        position: "left",
                        className: "w-full md:w-auto md:max-w-[90%] h-auto"
                    }}
                    className="bg-gradient-to-br from-[#E1D8D3] via-[#E0DDDD] to-[#E0DEDF] px-4 md:px-8 lg:px-24"
                />

                {/* Frequencies list */}
                <Fade duration={fadeDuration}>
                    <section className="bg-white py-8 md:py-16 px-4 md:px-8 lg:px-24">
                        <Title
                            firstLine={t.standardFrequenciesTitle}
                            secondLine={t.standardFrequenciesSubtitle}
                            className="mb-8"
                        />
                        <motion.div
                            className="space-y-4"
                            variants={staggerChildren}
                            initial="initial"
                            whileInView="animate"
                            viewport={{ once: true }}
                        >
                            {standardFrequencies.map((freq) => (
                                <motion.div
                                    key={freq.id}
                                    className="border-b border-gray-300 py-4"
                                    variants={fadeIn}
                                >
                                    <div
                                        className="flex items-center cursor-pointer justify-between"
                                        onClick={() => toggleFrequency(freq.id)}
                                    >
                                        <div className="flex items-center w-full">
                                            <MdChevronRight
                                                className={`w-5 h-5 transition-transform duration-300 ${expandedFrequency === freq.id ? 'rotate-90' : ''}`}
                                            />
                                            <div className="font-bold text-lg ml-2">
                                                {freq.id}
                                            </div>
                                            <div className="ml-4">
                                                {freq.title}
                                            </div>
                                        </div>
                                    </div>
                                    {expandedFrequency === freq.id && (
                                        <div className="mt-2 text-gray-600 pl-[44px]">
                                            <div className="mb-4">
                                                {freq.content}
                                            </div>
                                            <div className="mt-4">
                                                <img
                                                    src={freq.certificate}
                                                    alt={`${freq.id} Frequency Certificate`}
                                                    className="w-48 h-auto cursor-pointer hover:opacity-90 transition-opacity rounded shadow-md"
                                                    onClick={(e) => openCertificate(freq.id, e)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </section>
                </Fade>

                {/* Inner Compass */}
                <div className="relative w-full py-8 md:py-16 bg-gradient-to-br from-[#E1D8D3] via-[#E0DDDD] to-[#E0DEDF]">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col md:flex-row items-center max-w-7xl mx-auto">
                            {/* Left Text Block */}
                            <div className="md:flex-1 mb-8 md:mb-0 md:text-right md:pr-4">
                                <h2 className="text-3xl font-light mb-4">{t.innerCompassTitle}</h2>
                                <h2 className="text-3xl italic mb-6">{t.innerCompassSubtitle}</h2>
                            </div>

                            {/* Center Image */}
                            <div className="flex-none mb-8 md:mb-0">
                                <img
                                    src="/images/jewellery_collections_tournaline.png"
                                    alt="Tourmaline Crystal"
                                    className="w-[70px] h-auto rotate-180"
                                />
                            </div>

                            {/* Right Text Block */}
                            <div className="md:flex-1 mt-8 md:mt-0 md:pl-4 text-center md:text-left">
                                <h3 className="text-xl font-medium mb-4">{t.vikingsTitle}</h3>
                                <p className="text-sm leading-relaxed">
                                    {t.vikingsDescription}
                                </p>
                                <a href="/tourmalines/tourmalines" className="text-sm mt-4 inline-block hover:underline">
                                    {t.discoverMore}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lucky Star */}
                <TitleWithDescription
                    title={{
                        firstLine: t.luckyStarTitle,
                        secondLine: t.luckyStarSubtitle
                    }}
                    description={t.luckyStarDescription}
                    layout="column"
                    image={{
                        src: "/images/jewellery_tourmaline_sub_luckystar.png",
                        alt: "Tourmaline Crystal",
                        position: "right",
                        className: "w-48 h-auto justify-self-center"
                    }}
                    className="bg-gradient-to-r from-[#DD8288] to-[#8A2556] text-white px-4 md:px-8 lg:px-40"
                />

                <TitleWithDescription
                    title={{
                        firstLine: t.napoleonTitle,
                        secondLine: t.napoleonSubtitle
                    }}
                    description={t.napoleonDescription}
                    layout="row"
                    className="px-4 md:px-8 lg:px-24"
                />

                {/* The Collection */}
                <div className="pb-8 md:pb-16">
                    <Collection
                        items={getTourmalineCollectionItems(language)}
                        collectionId="tourmaline"
                        buildItemLink={(item) => `/jewellery/collections/tourmaline/${item.id}`}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="min-h-screen bg-white">
            {expandedCertificate && (
                <CertificateModal
                    certificateUrl={standardFrequencies.find(f => f.id === expandedCertificate)?.certificate || ''}
                    onClose={closeCertificate}
                />
            )}
            <BackgroundCover
                backgroundHeight={isMobile() ? 400 : undefined}
                imagePosition="center"
                backgroundImage="/images/jewellery_tourmaline_background.jpg"
                isWatchPage={true}
            >
                <div className="absolute top-1/2 left-[10%] md:left-[100px] translate-y-[200px] z-10">
                    <Title
                        firstLine={t.mainTitle}
                        secondLine={t.mainSubtitle}
                        className="text-white mt-[-80px] hidden md:block"
                        animate={true}
                    />
                </div>
            </BackgroundCover>
            <div className="w-full">
                {renderContent()}
            </div>
        </div>
    );
}

export default TourmalineCollection;
